import {
  LazyQueryHookOptions,
  useLazyQuery,
  useMutation
} from '@apollo/client';
import { useNavigate } from 'react-router';
import {
  ISlide,
  reducerSetCurrentTemplate,
  reducerSetInitialSelectedWizardDataSource,
  reducerSetNextSectionNumber,
  reducerSetPreviousSectionNumber,
  reducerSetReportList,
  reducerSetTemplateList,
  reducerUpdateActiveSlideContent,
  TVisualization
} from 'src/features/customReport';
import { useRef } from 'react';
import { useAppDispatch, useAppSelector } from 'src/app/hooks';
import { CUSTOM_REPORT } from 'src/route';
import {
  ADD_REPORT,
  CHANGE_STATUS_SCHEDULED_SLIDE_REPORT,
  CREATE_SCHEDULED_SLIDE_REPORT,
  DELETE_REPORT,
  DELETE_SLIDE,
  DUPLICATE_SLIDE_REPORT,
  EDIT_REPORT,
  EDIT_SCHEDULED_SLIDE_REPORT,
  PROCESS_WIZARD,
  REORDER_SLIDE,
  RESEND_SCHEDULED_SLIDE_REPORT
} from './graphql/customReport/mutation';
import {
  GET_REPORT_LIST,
  GET_REPORT_METRICS_OPTION,
  GET_REPORT_VISUALIZATION,
  GET_GENERATED_SLIDE_SUMMARY,
  GET_SCHEDULED_SLIDE_REPORT_HISTORY,
  GET_WIDGET_DATA,
  GET_WIZARD_TEMPLATE_DATA_SOURCES,
  GET_WIZARD_TEMPLATE_LIST
} from './graphql/customReport/query';
import {
  IScheduledSlideReportHistory,
  IScheduleSlideReport,
  IGetGeneratedSlideSummaryPayload,
  IGetGeneratedSlideSummaryResponse,
  ITemplate,
  ITemplateDetailResponse
} from 'src/models/customReport';
import { useAlert } from 'src/hooks/useAlert';

interface IResGetReportList {
  customReportV2_getCustomReport: {
    limit: number;
    page: number;
    totalRows: number;
    totalPages: number;
    result: Array<{
      id: string;
      displayName: string;
      createdAt: Date;
      logo?: string;
      start: string | Date;
      end: string | Date;
      schedule: IScheduleSlideReport;
    }>;
  };
}

interface IResGetTemplateList {
  customReportTemplate_getAllTemplate: {
    totalRows: number;
    totalPages: number;
    page: number;
    limit: number;
    result: Array<ITemplate>;
  };
}

interface IResAddReport {
  customReportV2_addCustomReport: {
    id: string;
    displayName: string;
    logo: string;
  };
}

interface IResProcessWizard {
  customReportTemplateWizard_processWizard: {
    id: string;
    name: string;
    displayName: string;
    createdAt: string;
  };
}

export interface IResGetSlides {
  customReportV2_getSlidesWithPagination: {
    limit: number;
    page: number;
    totalPages: number;
    totalRows: number;
    result: Array<ISlide>;
  };
}

export type TMetricCategory =
  | 'conversation'
  | 'content'
  | 'general'
  | 'campaign'
  | 'community'
  | 'comparison'
  | 'statistic'
  | 'general onm'
  | 'comparison onm'
  | 'statistic print media'
  | 'general print media'
  | 'statistic broadcast'
  | 'general broadcast'
  | 'comparison broadcast'
  | 'comparison print media'
  | 'google review'
  | 'comparison google review';

export interface IOptionItem {
  label: string;
  value: string;
}

export interface IMetricsOutput {
  category: TMetricCategory;
  metrics: Array<IOptionItem>;
}

interface IResMetrics {
  customReportV2_getMetrics: Array<IMetricsOutput>;
}

export interface IResVisualization {
  customReportV2_getVisualizations: Array<IOptionItem>;
}

interface IResGetScheduledSlideReportHistory {
  customReportV2_getScheduleSlideReportHistory: {
    totalRows: number;
    totalPages: number;
    page: number;
    limit: number;
    result: Array<IScheduledSlideReportHistory>;
  };
}

const useCustomReport = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { handleClickAlert } = useAlert();
  const shouldAutoSetVisualizationRef = useRef(false);
  const { activeSlide, activeField } = useAppSelector(
    (state) => state.storeCustomReport
  );
  const activeSlideType = activeSlide?.slideType;
  const activeContent =
    activeField >= 0
      ? activeSlide?.details[activeSlideType]?.contents[activeField]
      : undefined;

  const [getReportList, resGetReportList] = useLazyQuery<IResGetReportList>(
    GET_REPORT_LIST,
    {
      onCompleted: (data) => {
        dispatch(reducerSetReportList(data.customReportV2_getCustomReport));
      },
      fetchPolicy: 'no-cache'
    }
  );
  const [getMetricsOption, resGetMetricsOption] = useLazyQuery<IResMetrics>(
    GET_REPORT_METRICS_OPTION
  );
  const [getVisualization, resVisualization] = useLazyQuery<IResVisualization>(
    GET_REPORT_VISUALIZATION,
    {
      fetchPolicy: 'no-cache',
      onCompleted: (data) => {
        // Auto-set visualization to first item ONLY when user selected a metric
        if (!shouldAutoSetVisualizationRef.current) return;

        // add conditional to avoid auto-setting the disabled visualization
        const visualizationList = data.customReportV2_getVisualizations;
        const is4Columns = activeSlide.slideType === 'verticalFourContents';
        const isLeftOneLayout =
          activeSlide.slideType === 'leftOneFourContents' ||
          activeSlide.slideType === 'oneLeftSixContents' ||
          activeSlide.slideType === 'leftOneTwoContents';
        const isSamplePost = activeContent?.metric === 'sample_post';
        const isSampleArticle = activeContent?.metric === 'sample_article';

        let nextVisualization = visualizationList[0]?.value;

        if ((is4Columns || isLeftOneLayout) && isSamplePost)
          nextVisualization = 'sample_post_single';

        if ((is4Columns || isLeftOneLayout) && isSampleArticle)
          nextVisualization = 'sample_article_single';

        if (visualizationList && visualizationList.length > 0) {
          dispatch(
            reducerUpdateActiveSlideContent({
              data: {
                visualization: nextVisualization as TVisualization
              }
            })
          );
        }
        // Reset the flag after auto-setting
        shouldAutoSetVisualizationRef.current = false;
      }
    }
  );

  const [getWidgetData, resGetWidgetData] = useLazyQuery(GET_WIDGET_DATA);
  const useGetGeneratedSlideSummary = (
    options?: LazyQueryHookOptions<
      IGetGeneratedSlideSummaryResponse,
      IGetGeneratedSlideSummaryPayload
    >
  ) =>
    useLazyQuery<
      IGetGeneratedSlideSummaryResponse,
      IGetGeneratedSlideSummaryPayload
    >(GET_GENERATED_SLIDE_SUMMARY, {
      fetchPolicy: 'network-only',
      onError: () => {
        handleClickAlert({
          message: 'Error loading summary for this slide',
          severity: 'error'
        });
      },
      ...options
    });

  // --------------------------- Mutation ---------------------------------------
  const [addReport, resAddReport] = useMutation<IResAddReport>(ADD_REPORT, {
    onCompleted: (data) => {
      navigate(`/${CUSTOM_REPORT}/${data.customReportV2_addCustomReport.id}`);
    }
  });
  const [editReport, resEditReport] = useMutation(EDIT_REPORT);
  const [deleteReport, resDeleteReport] = useMutation(DELETE_REPORT);
  const [deleteSlide, resDeleteSlide] = useMutation(DELETE_SLIDE);
  const [reorderSlide, resReorderSlide] = useMutation(REORDER_SLIDE);

  const [getTemplateList, resGetTemplateList] =
    useLazyQuery<IResGetTemplateList>(GET_WIZARD_TEMPLATE_LIST, {
      onCompleted: (data) => {
        dispatch(
          reducerSetTemplateList(data.customReportTemplate_getAllTemplate)
        );
      },
      fetchPolicy: 'no-cache'
    });

  const [getTemplateDetail, resGetTemplateDetail] =
    useLazyQuery<ITemplateDetailResponse>(GET_WIZARD_TEMPLATE_DATA_SOURCES, {
      onCompleted: (data) => {
        dispatch(
          reducerSetCurrentTemplate({
            ...data.customReportTemplate_getTemplateDataSource,
            currentSectionPosition: 1
          })
        );
        const getSections =
          data.customReportTemplate_getTemplateDataSource.sections.map(
            (section) => {
              return section.dataSources.map((dataSource) => {
                return {
                  dataSource: dataSource.dataSourceType,
                  sourceType: dataSource.sourceType,
                  sectionId: section.id,
                  dataSourceId: dataSource.id,
                  primaryId: null,
                  templateId: data.customReportTemplate_getTemplateDataSource.id
                };
              });
            }
          );
        const getSectionsFlat = getSections.flat();
        dispatch(reducerSetInitialSelectedWizardDataSource(getSectionsFlat));
      },
      fetchPolicy: 'no-cache'
    });
  const [processWizard, resProcessWizard] = useMutation<IResProcessWizard>(
    PROCESS_WIZARD,
    {
      onCompleted: (data) => {
        navigate(
          `/${CUSTOM_REPORT}/${data.customReportTemplateWizard_processWizard.id}`
        );
      },
      onError: () => {
        handleClickAlert({
          horizontal: 'center',
          vertical: 'top',
          message: 'Error fetching process wizard',
          severity: 'error'
        });
      }
    }
  );

  const [duplicateSlideReport, resDuplicateSlideReport] =
    useMutation<IResProcessWizard>(DUPLICATE_SLIDE_REPORT, {
      onCompleted: () => {
        handleClickAlert({
          horizontal: 'center',
          vertical: 'top',
          message: 'Slide Report has successfully duplicated',
          severity: 'success'
        });
      },
      onError: () => {
        handleClickAlert({
          horizontal: 'center',
          vertical: 'top',
          message: 'Failed to duplicate slide report',
          severity: 'error'
        });
      }
    });

  const [createScheduledSlideReport, resCreateScheduledSlideReport] =
    useMutation(CREATE_SCHEDULED_SLIDE_REPORT, {
      onCompleted: () => {
        handleClickAlert({
          horizontal: 'center',
          vertical: 'top',
          message: 'Slide Report has successfully scheduled',
          severity: 'success'
        });
      },
      onError: () => {
        handleClickAlert({
          horizontal: 'center',
          vertical: 'top',
          message: 'Failed to schedule slide report',
          severity: 'error'
        });
      }
    });

  const [editScheduledSlideReport, resEditScheduledSlideReport] = useMutation(
    EDIT_SCHEDULED_SLIDE_REPORT,
    {
      onCompleted: () => {
        handleClickAlert({
          horizontal: 'center',
          vertical: 'top',
          message: 'Schedule Slide Report has successfully updated',
          severity: 'success'
        });
      },
      onError: () => {
        handleClickAlert({
          horizontal: 'center',
          vertical: 'top',
          message: 'Failed to update schedule slide report',
          severity: 'error'
        });
      }
    }
  );

  const [
    changeStatusScheduledSlideReport,
    resChangeStatusScheduledSlideReport
  ] = useMutation(CHANGE_STATUS_SCHEDULED_SLIDE_REPORT);

  const [getScheduledSlideReportHistory, resGetScheduledSlideReportHistory] =
    useLazyQuery<IResGetScheduledSlideReportHistory>(
      GET_SCHEDULED_SLIDE_REPORT_HISTORY,
      {
        fetchPolicy: 'network-only'
      }
    );

  const [resendScheduledSlideReport, resResendScheduledSlideReport] =
    useMutation(RESEND_SCHEDULED_SLIDE_REPORT, {
      onCompleted: () => {
        handleClickAlert({
          horizontal: 'center',
          vertical: 'top',
          message: 'Report has successfully resend.',
          severity: 'success'
        });
      },
      onError: () => {
        handleClickAlert({
          horizontal: 'center',
          vertical: 'top',
          message: 'Failed to resend report.',
          severity: 'error'
        });
      }
    });

  const handleChangeNextSection = () => dispatch(reducerSetNextSectionNumber());
  const handleChangePreviousSection = () =>
    dispatch(reducerSetPreviousSectionNumber());

  return {
    getReportList,
    resGetReportList,
    addReport,
    resAddReport,
    editReport,
    resEditReport,
    deleteReport,
    resDeleteReport,
    getMetricsOption,
    resGetMetricsOption,
    getVisualization,
    resVisualization,
    getWidgetData,
    resGetWidgetData,
    deleteSlide,
    resDeleteSlide,
    reorderSlide,
    resReorderSlide,
    getTemplateList,
    resGetTemplateList,
    getTemplateDetail,
    resGetTemplateDetail,
    handleChangeNextSection,
    handleChangePreviousSection,
    processWizard,
    resProcessWizard,
    shouldAutoSetVisualizationRef,
    resDuplicateSlideReport,
    duplicateSlideReport,
    createScheduledSlideReport,
    resCreateScheduledSlideReport,
    changeStatusScheduledSlideReport,
    resChangeStatusScheduledSlideReport,
    editScheduledSlideReport,
    resEditScheduledSlideReport,
    getScheduledSlideReportHistory,
    resGetScheduledSlideReportHistory,
    resendScheduledSlideReport,
    resResendScheduledSlideReport,
    useGetGeneratedSlideSummary
  };
};

export default useCustomReport;
